import { useEffect, useRef } from 'react';

interface UseEventListenerOptions {
  throttleMs?: number;
}

export function useEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (ev: WindowEventMap[K]) => any,
  element?: HTMLElement | Window | null | undefined,
  options?: boolean | AddEventListenerOptions | undefined,
  { throttleMs }: UseEventListenerOptions = {},
) {
  const savedHandler = useRef<(ev: WindowEventMap[K]) => any>(undefined);
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const targetElement = element || window;

    if (!(targetElement && targetElement.addEventListener)) {
      return;
    }

    const eventListener: EventListener = (event) => {
      const handler = savedHandler.current;

      if (!handler) {
        return;
      }

      if (!throttleMs) {
        handler(event as WindowEventMap[K]);
        return;
      }

      if (timeoutId.current !== null) {
        return;
      }

      timeoutId.current = setTimeout(() => {
        timeoutId.current = null;
        handler(event as WindowEventMap[K]);
      }, throttleMs);
    };

    targetElement.addEventListener(eventName, eventListener, options);

    return () => {
      targetElement.removeEventListener(eventName, eventListener);

      if (throttleMs && timeoutId.current !== null) {
        clearTimeout(timeoutId.current);

        timeoutId.current = null;
      }
    };
  }, [element, throttleMs]);
}
